/**
 * Edit this week's goals on Home (api lib/command/targets.ts). "Use my
 * numbers" fills in the last four full weeks' average plus a quarter.
 */
import { useEffect, useState } from "react";
import { Wand2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button, Input, Modal, ModalFooter, useToast } from "@/components/ui";

export interface WeeklyTargets { visitors: number; signups: number; paying: number; revenue: number }

const FIELDS: { key: keyof WeeklyTargets; label: string; prefix?: string }[] = [
  { key: "visitors", label: "Website visitors" },
  { key: "signups", label: "Signups" },
  { key: "paying", label: "New paying customers" },
  { key: "revenue", label: "Revenue", prefix: "$" },
];

export function TargetsModal({ open, current, onClose, onSaved }: {
  open: boolean;
  current: WeeklyTargets;
  onClose: () => void;
  onSaved: (t: WeeklyTargets) => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<Record<keyof WeeklyTargets, string>>({ visitors: "", signups: "", paying: "", revenue: "" });
  const [basis, setBasis] = useState<WeeklyTargets | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({ visitors: String(current.visitors), signups: String(current.signups), paying: String(current.paying), revenue: String(current.revenue) });
    setBasis(null);
    api.get<{ targets: WeeklyTargets; basis: WeeklyTargets }>("/command/targets/suggest").then((d) => setBasis(d.basis)).catch(() => setBasis(null));
  }, [open, current]);

  const useMine = async () => {
    try {
      const d = await api.get<{ targets: WeeklyTargets; basis: WeeklyTargets }>("/command/targets/suggest");
      setDraft({ visitors: String(d.targets.visitors), signups: String(d.targets.signups), paying: String(d.targets.paying), revenue: String(d.targets.revenue) });
      setBasis(d.basis);
    } catch {
      toast.error("Couldn't work out a suggestion");
    }
  };

  const save = async () => {
    const body: Partial<WeeklyTargets> = {};
    for (const f of FIELDS) {
      const n = Number(draft[f.key].replace(/[$,\s]/g, ""));
      if (!Number.isFinite(n) || n < 0) { toast.error(`${f.label} needs to be a number`); return; }
      body[f.key] = Math.round(n);
    }
    setSaving(true);
    try {
      const d = await api.put<{ targets: WeeklyTargets }>("/command/targets", body);
      onSaved(d.targets);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the goals");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="This week's goals" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-zinc-400">What a good week looks like. Home shows how far along each one is.</p>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="space-y-1">
              <span className="block text-xs font-medium text-zinc-400">{f.label}</span>
              <div className="relative">
                {f.prefix && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500">{f.prefix}</span>}
                <Input inputMode="numeric" value={draft[f.key]} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} className={f.prefix ? "pl-6" : undefined} />
              </div>
              {basis && <span className="block text-[11px] text-zinc-500">Lately about {f.prefix ?? ""}{basis[f.key].toLocaleString("en-US")} a week</span>}
            </label>
          ))}
        </div>
        <button type="button" onClick={useMine} className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-300 hover:text-indigo-200">
          <Wand2 className="h-3.5 w-3.5" /> Use my numbers (last four weeks, plus a quarter)
        </button>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={saving}>Save goals</Button>
      </ModalFooter>
    </Modal>
  );
}
